import React, { useState, useEffect, useRef } from 'react';


interface DynamicClockProps {
  hours: number;
  minutes: number;
  format?: '12h' | '24h';
  size?: 'sm' | 'md' | 'lg';
  showArabic?: boolean;
  showSpokenTime?: boolean;
  onTimeChange?: (hours: number, minutes: number) => void;
}

const DynamicClock: React.FC<DynamicClockProps> = ({
  hours: externalHours,
  minutes: externalMinutes,
  format = '24h',
  size = 'md',
  showArabic = true,
  showSpokenTime = true,
  onTimeChange
}) => {
  const [hours, setHours] = useState<number>(externalHours);
  const [minutes, setMinutes] = useState<number>(externalMinutes);
  const [showAnswer, setShowAnswer] = useState(true);
  const [isSpeakingEnglish, setIsSpeakingEnglish] = useState<boolean>(false);
  const [isSpeakingArabic, setIsSpeakingArabic] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<'hour' | 'minute' | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync with external props
  useEffect(() => {
    setHours(externalHours);
    setMinutes(externalMinutes);
  }, [externalHours, externalMinutes]);

  // Redraw canvas when time changes
  useEffect(() => {
    drawClock();
  }, [hours, minutes, format, size]);

  // Size mappings - larger for full screen
  const sizeMap = {
    sm: {
      canvas: 250,
      digital: 'text-3xl lg:text-4xl',
      arabicText: 'text-sm lg:text-base',
      spokenText: 'text-sm lg:text-base',
      iconSize: 'w-5 h-5',
      padding: 'p-3 lg:p-4'
    },
    md: {
      canvas: 400,
      digital: 'text-5xl lg:text-6xl',
      arabicText: 'text-base lg:text-lg',
      spokenText: 'text-base lg:text-lg',
      iconSize: 'w-6 h-6',
      padding: 'p-4 lg:p-6'
    },
    lg: {
      canvas: 550,
      digital: 'text-6xl lg:text-7xl',
      arabicText: 'text-lg lg:text-xl',
      spokenText: 'text-lg lg:text-xl',
      iconSize: 'w-7 h-7',
      padding: 'p-6 lg:p-8'
    }
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const currentSize = sizeMap[size];

  // Arabic number mapping
  const toArabicNumbers = (num: number): string => {
    const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return num.toString().split('').map(digit => arabicDigits[parseInt(digit)] || digit).join('');
  };

  // Stop any ongoing speech
  const stopSpeaking = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  // Speak English text
  const speakEnglish = (text: string) => {
    if (!window.speechSynthesis) {
      alert('Speech synthesis is not supported in your browser');
      return;
    }

    stopSpeaking();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => setIsSpeakingEnglish(true);
    utterance.onend = () => setIsSpeakingEnglish(false);
    utterance.onerror = () => setIsSpeakingEnglish(false);

    window.speechSynthesis.speak(utterance);
  };

  // Speak Arabic text
  const speakArabic = (text: string) => {
    if (!window.speechSynthesis) {
      alert('Speech synthesis is not supported in your browser');
      return;
    }

    stopSpeaking();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'ar-SA';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => setIsSpeakingArabic(true);
    utterance.onend = () => setIsSpeakingArabic(false);
    utterance.onerror = () => setIsSpeakingArabic(false);

    window.speechSynthesis.speak(utterance);
  };

  // Get English spoken time
  const getEnglishSpokenTime = (): string => {
    let displayHours = hours % 12 || 12;
    const period = hours >= 12 ? 'PM' : 'AM';

    if (minutes === 0) {
      return `The time is ${getEnglishHourName(displayHours)} o'clock ${period}`;
    } else if (minutes === 15) {
      return `The time is quarter past ${getEnglishHourName(displayHours)} ${period}`;
    } else if (minutes === 30) {
      return `The time is half past ${getEnglishHourName(displayHours)} ${period}`;
    } else if (minutes === 45) {
      const nextHour = (displayHours % 12) + 1;
      return `The time is quarter to ${getEnglishHourName(nextHour)} ${period}`;
    } else if (minutes < 30) {
      return `The time is ${minutes} minutes past ${getEnglishHourName(displayHours)} ${period}`;
    } else {
      const nextHour = (displayHours % 12) + 1;
      const minutesTo = 60 - minutes;
      return `The time is ${minutesTo} minutes to ${getEnglishHourName(nextHour)} ${period}`;
    }
  };

  // Get Arabic spoken time
  const getArabicSpokenTime = (): string => {
    let displayHours = hours % 12 || 12;
    const period = hours >= 12 ? 'مَسَاءً' : 'صَبَاحًا';

    const arabicHours: { [key: number]: string } = {
      1: 'الْوَاحِدَةُ',
      2: 'الثَّانِيَةُ',
      3: 'الثَّالِثَةُ',
      4: 'الرَّابِعَةُ',
      5: 'الْخَامِسَةُ',
      6: 'السَّادِسَةُ',
      7: 'السَّابِعَةُ',
      8: 'الثَّامِنَةُ',
      9: 'التَّاسِعَةُ',
      10: 'الْعَاشِرَةُ',
      11: 'الْحَادِيَةُ عَشْرَةَ',
      12: 'الثَّانِيَةُ عَشْرَةَ'
    };

    if (minutes === 0) {
      return `السَّاعَةُ ${arabicHours[displayHours]} ${period}`;
    } else if (minutes === 15) {
      return `السَّاعَةُ ${arabicHours[displayHours]} وَالرُّبْعُ ${period}`;
    } else if (minutes === 30) {
      return `السَّاعَةُ ${arabicHours[displayHours]} وَالنِّصْفُ ${period}`;
    } else if (minutes === 45) {
      const nextHour = (displayHours % 12) + 1;
      return `السَّاعَةُ ${arabicHours[nextHour]} إِلَّا رُبْعًا ${period}`;
    } else if (minutes < 30) {
      return `السَّاعَةُ ${arabicHours[displayHours]} وَ ${toArabicNumbers(minutes)} دَقِيقَةً ${period}`;
    } else {
      const nextHour = (displayHours % 12) + 1;
      const minutesTo = 60 - minutes;
      return `السَّاعَةُ ${arabicHours[nextHour]} إِلَّا ${toArabicNumbers(minutesTo)} دَقِيقَةً ${period}`;
    }
  };

  const getEnglishHourName = (hour: number): string => {
    const hourNames: { [key: number]: string } = {
      1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six',
      7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve'
    };
    return hourNames[hour];
  };

  const getArabicTimeText = (): string => {
    let displayHours = hours;
    let period = '';

    if (format === '12h') {
      period = hours >= 12 ? ' مساءً' : ' صباحاً';
      displayHours = hours % 12 || 12;
    }

    const hourText = toArabicNumbers(displayHours);
    const minuteText = toArabicNumbers(minutes);
    const periodText = format === '12h' ? period : '';

    return `${hourText}:${minuteText}${periodText}`;
  };

  const getEnglishTimeText = (): string => {
    let displayHours = hours;
    let period = '';

    if (format === '12h') {
      period = hours >= 12 ? ' PM' : ' AM';
      displayHours = hours % 12 || 12;
    }

    const hourText = displayHours.toString().padStart(2, '0');
    const minuteText = minutes.toString().padStart(2, '0');

    return `${hourText}:${minuteText}${period}`;
  };

  const toggleShowAnswer = () => {
    setShowAnswer(!showAnswer)
  }

  const formatDisplayTime = (): string => {
    let displayHours = hours;
    let ampm = '';

    if (format === '12h') {
      ampm = hours >= 12 ? ' PM' : ' AM';
      displayHours = hours % 12 || 12;
    }

    const formattedHours = displayHours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}${ampm}`;
  };

  // Convert angle to time
  const angleToTime = (angleDeg: number, isHour: boolean): number => {
    let value;
    if (isHour) {
      // Hour hand: 360° = 12 hours = 30° per hour, plus 0.5° per minute
      value = (angleDeg + 90) / 30;
      value = ((value % 12) + 12) % 12;
      if (value === 0) value = 12;
      // Convert to 24-hour format if needed
      const currentHour24 = hours >= 12 ? value + 12 : value;
      return value;
    } else {
      // Minute hand: 360° = 60 minutes = 6° per minute
      value = (angleDeg + 90) / 6;
      value = ((value % 60) + 60) % 60;
      return Math.round(value);
    }
  };

  // Get hand angle from time
  const getHourAngle = (): number => {
    return ((hours % 12) * 30 + minutes * 0.5 - 90) * Math.PI / 180;
  };

  const getMinuteAngle = (): number => {
    return (minutes * 6 - 90) * Math.PI / 180;
  };

  // Check if point is near a hand
  const getHitHand = (x: number, y: number, centerX: number, centerY: number, radius: number): 'hour' | 'minute' | null => {
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Hour hand hit area
    const hourAngle = getHourAngle();
    const hourEndX = centerX + radius * 0.5 * Math.cos(hourAngle);
    const hourEndY = centerY + radius * 0.5 * Math.sin(hourAngle);
    const hourDistToEnd = Math.sqrt(Math.pow(x - hourEndX, 2) + Math.pow(y - hourEndY, 2));

    // Minute hand hit area
    const minuteAngle = getMinuteAngle();
    const minuteEndX = centerX + radius * 0.7 * Math.cos(minuteAngle);
    const minuteEndY = centerY + radius * 0.7 * Math.sin(minuteAngle);
    const minuteDistToEnd = Math.sqrt(Math.pow(x - minuteEndX, 2) + Math.pow(y - minuteEndY, 2));

    const hitRadius = radius * 0.08;

    if (minuteDistToEnd < hitRadius) {
      return 'minute';
    } else if (hourDistToEnd < hitRadius) {
      return 'hour';
    }

    return null;
  };

  // Handle touch/mouse start
  const handleStart = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const canvasX = (clientX - rect.left) * scaleX;
    const canvasY = (clientY - rect.top) * scaleY;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = canvas.width * 0.45;

    const hit = getHitHand(canvasX, canvasY, centerX, centerY, radius);
    if (hit) {
      setIsDragging(hit);
    }
  };

  // Handle touch/mouse move
  const handleMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const canvasX = (clientX - rect.left) * scaleX;
    const canvasY = (clientY - rect.top) * scaleY;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    const dx = canvasX - centerX;
    const dy = canvasY - centerY;
    let angle = Math.atan2(dy, dx) * 180 / Math.PI;

    if (isDragging === 'hour') {
      let newHour = angleToTime(angle, true);
      if (newHour === 0) newHour = 12;

      // Preserve AM/PM based on current hours
      let newHour24 = newHour;
      if (hours >= 12 && newHour !== 12) {
        newHour24 = newHour + 12;
      } else if (hours < 12 && newHour === 12) {
        newHour24 = 0;
      } else if (hours >= 12 && newHour === 12) {
        newHour24 = 12;
      }

      setHours(newHour24);
      onTimeChange?.(newHour24, minutes);
    } else if (isDragging === 'minute') {
      let newMinute = angleToTime(angle, false);
      newMinute = Math.round(newMinute / 5) * 5; // Snap to 5-minute intervals
      setMinutes(newMinute);
      onTimeChange?.(hours, newMinute);
    }
  };

  // Handle end
  const handleEnd = () => {
    setIsDragging(null);
  };

  const drawClock = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = canvas.width;
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size * 0.45;

    ctx.clearRect(0, 0, size, size);

    // Draw clock face
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = Math.max(2, size / 150);
    ctx.stroke();

    // Draw hour numbers
    ctx.fillStyle = '#333333';
    ctx.font = `bold ${Math.max(14, size * 0.08)}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 1; i <= 12; i++) {
      const angle = (i * 30 - 90) * Math.PI / 180;
      const x = centerX + radius * 0.75 * Math.cos(angle);
      const y = centerY + radius * 0.75 * Math.sin(angle);
      ctx.fillText(i.toString(), x, y);
    }

    // Draw hour marks
    for (let i = 0; i < 60; i++) {
      const angle = (i * 6 - 90) * Math.PI / 180;
      const isHourMark = i % 5 === 0;
      const startRadius = radius - (isHourMark ? size / 25 : size / 37);
      const endRadius = radius - size / 60;

      const x1 = centerX + startRadius * Math.cos(angle);
      const y1 = centerY + startRadius * Math.sin(angle);
      const x2 = centerX + endRadius * Math.cos(angle);
      const y2 = centerY + endRadius * Math.sin(angle);

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = '#666666';
      ctx.lineWidth = isHourMark ? Math.max(2, size / 100) : Math.max(1, size / 150);
      ctx.stroke();
    }

    // Draw hour hand with glow effect when dragging
    const hourAngle = getHourAngle();
    const hourLength = radius * 0.5;

    if (isDragging === 'hour') {
      ctx.shadowBlur = 15;
      ctx.shadowColor = '#ff6b6b';
    }
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(centerX + hourLength * Math.cos(hourAngle), centerY + hourLength * Math.sin(hourAngle));
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = Math.max(5, size / 60);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Draw minute hand with glow effect when dragging
    const minuteAngle = getMinuteAngle();
    const minuteLength = radius * 0.7;

    if (isDragging === 'minute') {
      ctx.shadowBlur = 15;
      ctx.shadowColor = '#4ecdc4';
    }
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(centerX + minuteLength * Math.cos(minuteAngle), centerY + minuteLength * Math.sin(minuteAngle));
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = Math.max(3, size / 80);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Draw center dot
    ctx.beginPath();
    ctx.arc(centerX, centerY, Math.max(6, size / 50), 0, 2 * Math.PI);
    ctx.fillStyle = '#333333';
    ctx.fill();

    // Draw hint text for touch
    if (isMobile) {
      ctx.font = `${Math.max(10, size * 0.03)}px Arial`;
      ctx.fillStyle = '#999999';
      ctx.textAlign = 'center';
      ctx.fillText('Touch and drag the hands', centerX, centerY + radius + 15);
    }
  };

  return (
    <div ref={containerRef} className={`flex flex-col items-center justify-center gap-4 lg:gap-8 w-full h-full ${currentSize.padding}`}>
      {/* Clock Canvas with Touch Events */}
      <div className="flex justify-center items-center w-full relative">
        <canvas
          ref={canvasRef}
          width={currentSize.canvas}
          height={currentSize.canvas}
          className="bg-white rounded-full shadow-2xl transition-all duration-300 cursor-grab active:cursor-grabbing touch-none"
          style={{ width: 'auto', height: 'auto', maxWidth: '90vw', maxHeight: '90vw' }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />
      </div>

      <button
        onClick={() => toggleShowAnswer()}
        className={`absolute top-20 left-10 text-xl ${showAnswer
            ? 'animate-pulse'
            : ''
          }`}>👁</button>

      {showAnswer &&
        <div>

          <div className={`flex flex-wrap font-arabic bg-white/95 backdrop-blur-sm p-2 pt-5 rounded-xl lg:rounded-2xl shadow-lg text-gray-800 ${currentSize.digital} tracking-wider text-center flex-1`}>
            <button
              onClick={() => speakArabic(getArabicSpokenTime())}
              disabled={isSpeakingArabic}
              className={`absolute top-0 left-1 text-xl ${isSpeakingArabic
                  ? 'animate-pulse'
                  : ''
                }`}>🔊</button>
           <div className="flex px-3"> {getArabicSpokenTime()}</div>
          </div>


        </div>
      }







      {/* Digital Time Display with Compact Voice Icons */}
      <div className="flex items-center justify-center gap-3 lg:gap-4 w-full max-w-3xl mx-auto">
        {showAnswer && showArabic && (
          <div className="flex gap-3 lg:gap-4 justify-center w-full max-w-3xl mx-auto">
            <div className="flex-1 bg-white/90 backdrop-blur-sm rounded-lg p-2 lg:p-3 shadow-md text-center">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider">English</div>
              <div className={`font-mono font-bold text-gray-800 ${currentSize.arabicText}`}>
                <button
                  onClick={() => speakArabic(getEnglishSpokenTime())}
                  disabled={isSpeakingArabic}
                  className={`absolute top-1 left-2 text-xl ${isSpeakingArabic
                      ? 'animate-pulse'
                      : ''
                    }`}>🔊</button>
                {getEnglishTimeText()}
              </div>
            </div>
            <div className="flex-1 bg-white/90 backdrop-blur-sm rounded-lg p-2 lg:p-3 shadow-md text-center" dir="rtl">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider">العربية</div>
              <div className={`font-mono font-bold text-gray-800 ${currentSize.arabicText}`}>
                <button
                  onClick={() => speakArabic(getArabicSpokenTime())}
                  disabled={isSpeakingArabic}
                  className={`absolute top-1 left-2 text-xl ${isSpeakingArabic
                      ? 'animate-pulse'
                      : ''
                    }`}>🔊</button>
                {getArabicTimeText()}
              </div>
            </div>
          </div>
        )}


      </div>

    </div>
  );
};

export default DynamicClock;